import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {WebPViaServerDialogComponent} from "../../dialogs/webp-via-server-dialog/webp-via-server-dialog.component";
import {DbModalService} from "../../../../../modules/modal/modal";
import {filter, first, switchMap, takeUntil} from "rxjs/operators";
import {BoosterService} from "../../services/booster.service";
import {FormControl, FormGroup} from "@angular/forms";
import {Store} from "@ngrx/store";
import * as fromStore from '../../../../../store';
import {Subject} from "rxjs";
import {UserSubscription} from "../../../../../user/user-subscription";
import {DialogService} from "../../../../../../ng2-bootstrap-modal/dialog.service";
import {TypeUtils} from "../../../../../shared/utils/type-utils";
import {ActivatedRoute, Router} from "@angular/router";
import {Observable} from "rxjs";
import {ActivityAction} from "../../../../../activities/activity";
import {IBoosterStats} from "../../../../../store/reducers/booster.reducer";
import {NotifierComponent} from "../../../../../shared/components/notifier/notifier.component";
import {Notification} from "../../../../../shared/components/notifier/notification";
import {TextUtils} from "../../../../../shared/utils/text-utils";
import {NotificationType} from "../../../../../shared/components/notifier/notification-type";
import {GoogleAnalyticsService} from "../../../../../shared/services/google-analytics/google-analytics.service";
import {Utils} from "../../../../../shared/utils/utils";
import {User} from "../../../../../user/user";
import {
  OneWeekFreeDemoUpgradeComponent
} from "../../../../../shared/components/dialogs/one-month-free/components/one-week-free-demo-upgrade/one-week-free-demo-upgrade.component";
import {combineLatest} from "rxjs";
import {OneMonthFreeComponent} from "../../../../../shared/components/dialogs/one-month-free/one-month-free.component";

@Component({
    selector: 'db-optimize-images',
    templateUrl: './optimize-images.component.html',
    styleUrls: ['./optimize-images.component.scss'],
    standalone: false
})
export class OptimizeImagesComponent implements OnInit, OnDestroy {

  @Input() currentSubscription: UserSubscription;
  public optimizerDisabled: boolean;
  public boosterSettings: any;

  public websiteId: number;
  public websiteHosted: boolean;
  public websiteServerType: string;
  public imageOptimizerSettings: FormGroup = new FormGroup({
    'autoOptimize': new FormControl(null),
    'webpStatus': new FormControl(null),
    'pictureWebpStatus': new FormControl(null)
  });
  public loading: boolean = true;
  public stats: IBoosterStats;
  public error: boolean = false;
  public webpServerConfigIsActive: boolean = true;
  public pageLayout: boolean = false;
  public loadingWebpFormat: boolean = false;
  public loadingAutoOptimize: boolean = false;
  public showPercentOffer: boolean;
  public user: User;
  public optimizeImagesStats$: Observable<any>;
  demoUser: boolean = false;

  private _destroyStream$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private _router: Router,
    private _dbModal: DbModalService,
    private _boosterService: BoosterService,
    private _store: Store<fromStore.DbStoreState>,
    private _dialogService: DialogService,
    private _route: ActivatedRoute,
    private _googleAnalyticsService: GoogleAnalyticsService,
  ) {}

  ngOnInit() {
    this._getUser();
    this._initData();
    this._showPercentOffer();
    this._addEventHandlers();
  }

  ngOnDestroy() {
    this._destroyStream$.next(true);
    this._destroyStream$.complete();
  }

  private _getUser(): void {
    this._store.select(fromStore.getUserLoaded)
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() =>
          combineLatest(
            this._store.select(fromStore.getUser),
            this._store.select(fromStore.getUserSubscription)
          )
        )
      )
      .subscribe(([user, userSubscription]) => {
        this.demoUser = Utils.isDemoUser(userSubscription, user);
        this.user = user;
      });
  }

  private _showPercentOffer() {
    const percentOfferEndDate = new Date("2024-01-30 18:00:00").getTime();
    const datesDiff = Utils.getTimeDiffFromToday(percentOfferEndDate);
    this.showPercentOffer = datesDiff && this.currentSubscription && this.currentSubscription.trialBoosterClient;
  }

  private _initData(): void {
    this.pageLayout = this._route.snapshot.data['pageId'] === 'image-optimizer';
    const website = this._route.snapshot.data['pageId'] === 'image-optimizer' ? this._route.snapshot.parent.data['website'] : this._route.snapshot.parent.parent.data['website'];
    this.websiteId = website.id;
    this.websiteHosted = !!website.domain_hash;
    this.websiteServerType = website.serverType;
  }

  private _addEventHandlers(): void {
    this._store.select(fromStore.getOngoingActivities)
      .subscribe((data: any) => {
        const optimizerActivity = data && data.length && data.find(action => [ActivityAction.OPTIMIZE].includes(<any>action.action) );
        if (!TypeUtils.isEmpty(optimizerActivity)) {
          if ( optimizerActivity.status === 'pending' && TypeUtils.isEmpty(optimizerActivity.meta)) {
            this.loading = true;
          }
          if (optimizerActivity.status === 'done' || optimizerActivity.status === 'failed' || optimizerActivity.status === 'canceled') {
            this._getOptimizerStats();
            this._getWebsiteBoosterStats();
          }
        }
      });

    if (this.pageLayout) {
      this._getImageOptimizer();
    }
    else {
      this._getWebsiteBoosterStats();
    }
    this._getBoosterSettings();
    this._getAutoOptimize();
  }

  private _getImageOptimizer(): void {
    this._store.select(fromStore.getWebsiteImageOptimizerPlugin(this.websiteId))
      .pipe(takeUntil(this._destroyStream$))
      .subscribe((imageOptimizerPlugin): void => {
        if (imageOptimizerPlugin) {
          this.optimizerDisabled = !imageOptimizerPlugin
            || (imageOptimizerPlugin && (!imageOptimizerPlugin.paidVersionInstalled || !imageOptimizerPlugin.active));
          if (!this.optimizerDisabled) {
            this.loading = true;
            this._getOptimizerStats();
          }
        }
      });
  }

  private _getBoosterSettings(): void {
    this._store.select(fromStore.getWebsiteWebpStatus(this.websiteId))
      .pipe(
        takeUntil(this._destroyStream$)
      )
      .subscribe(webpStatus => {
        if (!TypeUtils.isNull(webpStatus)) {
          this.loadingWebpFormat = false;
          this.loading = this.stats?.compressIsRunning;
          this.boosterSettings = webpStatus;
          this.imageOptimizerSettings.controls['webpStatus'].setValue(TypeUtils.isDefined(this.boosterSettings.webpStatus) ? this.boosterSettings.webpStatus : false);
          this.imageOptimizerSettings.controls['pictureWebpStatus'].setValue(TypeUtils.isDefined(this.boosterSettings.pictureWebpStatus) ? this.boosterSettings.pictureWebpStatus : false);
          this.webpServerConfigIsActive = TypeUtils.isDefined(this.boosterSettings) && !TypeUtils.isNull(this.boosterSettings) && TypeUtils.isDefined(this.boosterSettings.webpStatus) && !this.boosterSettings.webpStatus
            && TypeUtils.isDefined(this.boosterSettings.webpDeliveryWorking) && !!this.boosterSettings.webpDeliveryWorking;
        }
      });
  }

  private _getAutoOptimize(): void {
    this._store.select(fromStore.getImageOptimizerAutoOptimizeEnabled(this.websiteId))
      .pipe(
        takeUntil(this._destroyStream$)
      )
      .subscribe(autoOptimizeEnabled => {
        if (!TypeUtils.isNull(autoOptimizeEnabled) && TypeUtils.isDefined(autoOptimizeEnabled)) {
          this.imageOptimizerSettings.controls['autoOptimize'].setValue(autoOptimizeEnabled);
        }
        this.loadingAutoOptimize = false;
      });
  }

  private _getWebsiteBoosterStats(): void {
    this.optimizeImagesStats$ = this._store.select(fromStore.getImageOptimizerStats(this.websiteId))
      .pipe(
        takeUntil(this._destroyStream$)
      );

    this.optimizeImagesStats$
      .subscribe(stats => {
        if (!TypeUtils.isNull(stats)) {
          this.stats = stats;
          this.error = false;
          this.loading = this.stats.compressIsRunning || false;
        }
        else {
          this.loading = false;
          this.error = true;
        }
      });
  }

  private _getOptimizerStats(): void {
    this._store.select(fromStore.getImageOptimizerStats(this.websiteId))
      .pipe(
        takeUntil(this._destroyStream$)
      )
      .subscribe((stats): void => {
        this.loading = false;
        if (!TypeUtils.isNull(stats)) {
          this.stats = stats;
          this.loading = this.stats.compressIsRunning || false;
          this._store.dispatch(new fromStore.SetBoosterStats({
            websiteId: this.websiteId,
            stats: stats
          }));
        }
      });
  }

  public setAutoOptimize(): void {
    if (this.currentSubscription
      && ((this.currentSubscription.trialClient && this.demoUser)
        || this.currentSubscription.bundleClient
        || this.currentSubscription.oneProductClient)) {
      this.imageOptimizerSettings.controls['autoOptimize'].setValue(false);

      if (this.demoUser) {
        const firstTime = TypeUtils.isNull(localStorage.getItem('dbShowFirstTime'));
        const options = {
          overflowHidden: false,
        };
        if (!firstTime) {
          options['width'] = 1240;
        } else {
          options['width'] = 460;
          options['height'] = 600;
        }
        const modal = this._dbModal.open(OneWeekFreeDemoUpgradeComponent, options);
        modal.componentInstance.showFirstTime = firstTime;
      } else {
        const modal = this._dbModal.open(OneMonthFreeComponent, {height: 600, width: 940});
        modal.componentInstance.mode = 'feature';
      }
      return;
    }
    this.loadingAutoOptimize = true;
    this._boosterService.optimizerSettingsStore(this.websiteId, this.imageOptimizerSettings.value.autoOptimize)
      .pipe(
        first(response => !!response, null)
      )
      .subscribe((response) => {
        if (response.success) {
          this._store.dispatch(new fromStore.LoadBoosterAutoOptimize({websiteId: this.websiteId}));
          NotifierComponent.notify(new Notification(response.message, NotificationType.SUCCESS));
        }
        else {
          NotifierComponent.notify(new Notification(TextUtils.getText('text_somethingWentWrongDot'), NotificationType.DANGER));
        }
        this.loadingAutoOptimize = false;
      });
  }


  public setWebpStatus(): void {
    this.loadingWebpFormat = true;
    this._boosterService.setWebpStatus(this.websiteId, this.imageOptimizerSettings.value.webpStatus, this.imageOptimizerSettings.value.pictureWebpStatus)
      .pipe(
        first(response => !!response, null)
      )
      .subscribe(() => {
        this._store.dispatch(new fromStore.LoadBoosterData({websiteId: this.websiteId}));
        this.loadingWebpFormat = true;
      });
  }

  public showWebPServerDialog(): void {
    const modal = this._dbModal.open(WebPViaServerDialogComponent);
    modal.componentInstance.serverType = this.websiteServerType;
  }

  public optimizeImages(): void {
    if (this.currentSubscription
      && (this.currentSubscription.bundleClient
        || this.currentSubscription.oneProductClient)) {
      const modal = this._dbModal.open(OneMonthFreeComponent, {height: 600, width: 940});
      modal.componentInstance.mode = 'feature';
      return;
    }
    this.loading = true;
    this._boosterService.optimizeImages(this.websiteId);
  }

  public getWebpStatus(): void {
    this.loadingWebpFormat = true;
    this._store.dispatch(new fromStore.LoadBoosterData({websiteId: this.websiteId}));
    setTimeout( () => {
      NotifierComponent.notify(new Notification('Webp status checked successfully', NotificationType.SUCCESS));
      this.loadingWebpFormat = false;
    }, 3000);
  }

}
